/**
 * Customer PWA glue — service worker registration + web push subscription
 * helpers. Sub-spec 12 (customer side, browser-driven).
 *
 * The server-side push channel (FCM) and the device-tokens table both shipped
 * in sub-spec 12 Plan 2. This module wires the browser ↔ server boundary:
 *
 *   - registerCustomerSW()        — register /customer-sw.js + periodic
 *                                   update checks + emit an event when an
 *                                   update is waiting.
 *   - getDeferredInstallPrompt()  — singleton accessor for the captured
 *                                   beforeinstallprompt event.
 *   - getInstallVisits()/...      — localStorage-based "visit counter" so
 *                                   the install banner only nags once we know
 *                                   the customer is sticking around.
 *   - registerPushSubscription()  — Notification.requestPermission →
 *                                   pushManager.subscribe → POST to
 *                                   /api/customer/device-tokens.
 *   - unregisterPushSubscription()
 *
 * Every API is browser-safe (checks for `navigator.serviceWorker`, etc.) so
 * tests + SSR don't blow up.
 */

import { customerApi } from './customer_api';

const SW_URL = '/customer-sw.js';
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const VISIT_STORAGE_KEY = 'kennel.pwa.visits';
const INSTALL_DISMISSED_KEY = 'kennel.pwa.installDismissed';
const PUSH_PROMPT_DISMISSED_KEY = 'kennel.pwa.pushDismissed';

// ---------------------------------------------------------------------------
// Service worker registration + update detection
// ---------------------------------------------------------------------------

export type SwUpdateListener = (registration: ServiceWorkerRegistration) => void;

const updateListeners = new Set<SwUpdateListener>();
let currentRegistration: ServiceWorkerRegistration | null = null;

export function onSwUpdate(listener: SwUpdateListener): () => void {
  updateListeners.add(listener);
  return () => {
    updateListeners.delete(listener);
  };
}

function notifyUpdate(reg: ServiceWorkerRegistration) {
  for (const l of updateListeners) {
    try {
      l(reg);
    } catch {
      // ignore listener errors
    }
  }
}

export async function registerCustomerSW(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined') return null;
  if (!('serviceWorker' in navigator)) return null;
  // Skip during local dev unless explicitly opted in — Vite's HMR + SW
  // caching of /index.html make for a confusing local experience.
  if (
    window.location.hostname === 'localhost' &&
    !window.localStorage.getItem('kennel.pwa.devMode')
  ) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
    currentRegistration = reg;

    // If a worker is already waiting at registration time, surface it now.
    if (reg.waiting) notifyUpdate(reg);

    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          notifyUpdate(reg);
        }
      });
    });

    // Periodic update check.
    window.setInterval(() => {
      reg.update().catch(() => {
        // ignore — typically just no network
      });
    }, UPDATE_CHECK_INTERVAL_MS);

    // When the controller changes (after a SKIP_WAITING + reload), reload.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    return reg;
  } catch (err) {
    console.warn('Service worker registration failed:', err);
    return null;
  }
}

export function activatePendingUpdate(): void {
  const reg = currentRegistration;
  if (!reg) return;
  const waiting = reg.waiting;
  if (!waiting) return;
  waiting.postMessage({ type: 'SKIP_WAITING' });
  // Reload happens on the controllerchange listener above.
}

// ---------------------------------------------------------------------------
// Visit counter (localStorage)
// ---------------------------------------------------------------------------

export function bumpInstallVisit(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const prev = Number.parseInt(window.localStorage.getItem(VISIT_STORAGE_KEY) || '0', 10);
    const next = (Number.isFinite(prev) ? prev : 0) + 1;
    window.localStorage.setItem(VISIT_STORAGE_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

export function getInstallVisits(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const v = Number.parseInt(window.localStorage.getItem(VISIT_STORAGE_KEY) || '0', 10);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

export function dismissInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function isInstallPromptDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage.getItem(INSTALL_DISMISSED_KEY);
  } catch {
    return false;
  }
}

export function dismissPushPrompt(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function isPushPromptDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// beforeinstallprompt singleton
// ---------------------------------------------------------------------------

/**
 * The Chrome / Edge `beforeinstallprompt` event the browser fires when the
 * PWA install heuristics are satisfied. We capture it once at app boot so the
 * InstallPrompt component can call `.prompt()` whenever it decides to.
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<(p: BeforeInstallPromptEvent | null) => void>();

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

export function onInstallPromptAvailable(
  listener: (p: BeforeInstallPromptEvent | null) => void,
): () => void {
  installListeners.add(listener);
  return () => {
    installListeners.delete(listener);
  };
}

export function attachInstallListeners(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    for (const l of installListeners) {
      try {
        l(deferredPrompt);
      } catch {
        // ignore
      }
    }
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    for (const l of installListeners) {
      try {
        l(null);
      } catch {
        // ignore
      }
    }
    // Fire-and-forget analytics — fall back silently if the route isn't
    // wired in this deploy (analytics endpoint is OPTIONAL).
    customerApi('/api/customer/analytics', {
      method: 'POST',
      body: JSON.stringify({ event: 'pwa_installed', at: new Date().toISOString() }),
    }).catch(() => {
      // ignore — no analytics endpoint yet.
    });
  });
}

// ---------------------------------------------------------------------------
// Push subscription
// ---------------------------------------------------------------------------

export type PushPermission = NotificationPermission | 'unsupported';

export function getPushPermission(): PushPermission {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  return Notification.permission;
}

export interface CurrentPushSubscription {
  endpoint: string;
  registered: boolean;
}

/**
 * Subscribe the customer to web push and POST the resulting endpoint as a
 * `web_push` device-token to /api/customer/device-tokens.
 *
 * Returns null + a permission state if the user declined or the browser
 * doesn't support push.
 */
export async function registerPushSubscription(): Promise<
  { ok: true; subscription: PushSubscription } | { ok: false; reason: PushPermission | 'error' }
> {
  if (getPushPermission() === 'unsupported') return { ok: false, reason: 'unsupported' };

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm };

  try {
    const reg = (await navigator.serviceWorker.ready) ?? null;
    if (!reg) return { ok: false, reason: 'error' };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const vapid = readVapidPublicKey();
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        ...(vapid ? { applicationServerKey: urlBase64ToUint8Array(vapid) } : {}),
      });
    }

    // POST the subscription endpoint (or FCM token if exposed) to the server.
    // For VAPID we send the full subscription JSON; the server stores the
    // endpoint as `token` and platform `web_push`.
    const subJson = sub.toJSON();
    const token = JSON.stringify(subJson);

    await customerApi('/api/customer/device-tokens', {
      method: 'POST',
      body: JSON.stringify({ platform: 'web_push', token }),
    });

    return { ok: true, subscription: sub };
  } catch (err) {
    console.warn('Push subscription failed:', err);
    return { ok: false, reason: 'error' };
  }
}

export async function unregisterPushSubscription(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch {
    // ignore
  }
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

// VAPID public key sourced from a meta tag if the operator has wired one.
// The Plan-5-deferred path (sub-spec 12 Plan 5) installs VAPID; until then
// this returns null and we let pushManager.subscribe fall through with no
// applicationServerKey (FCM-style web push, where supported).
function readVapidPublicKey(): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('meta[name="kennel-vapid-public-key"]');
  if (!el) return null;
  const v = el.getAttribute('content');
  return v && v.length > 0 ? v : null;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
